// THE WRITING PROFILE — how this person and the operator actually write to each other.
//
// Nothing here is a default. The greeting is the one the operator has actually used with this
// person, verbatim; the register is a reading of the other side's own lines with the number of
// lines that support it; the medians are measured. Under two messages the server says
// `insufficient` and EVERY value renders `fv--unknown` "not collected" — dotted, quiet, and
// visibly different from a zero. A profile guessed from one mail is worse than no profile,
// because a draft would then confidently address a stranger by the wrong form.
//
// The counts and medians carry NO claim mark: arithmetic makes no claim. The register does
// carry ◇, because it is a reading of evidence that nobody has held.
import { useCallback } from 'react';

import { Att, Fig, Sheet } from './PageKit.js';
import { usePoll } from './usePoll.js';
import { useT } from '../i18n/index.js';
import { writingProfile } from '../api/mail.js';
import './WritingProfile.de.js';
import './WritingProfile.fr.js';

export interface WritingProfileProps {
  personId: number;
}

const REGISTER_WORD: Record<string, string> = { sie: 'formal (Sie / vous)', du: 'familiar (du / tu)' };
const LANGUAGE_WORD: Record<string, string> = { de: 'German', fr: 'French', en: 'English', it: 'Italian' };

/** The one honest way to say "nobody counted this". Never a dash, never a zero. */
function Unknown({ label }: { label: string }) {
  return <span className="fv--unknown">{label}</span>;
}

export default function WritingProfile({ personId }: WritingProfileProps) {
  const t = useT();
  const fetcher = useCallback(() => writingProfile(personId), [personId]);
  const poll = usePoll(fetcher, 120_000);
  const profile = poll.data;

  if (poll.error && !profile) {
    return (
      <Sheet pad>
        <p className="zone-empty">{t('The writing profile could not be read.')} {poll.error}</p>
      </Sheet>
    );
  }

  if (!profile) {
    return (
      <Sheet pad>
        <p className="zone-empty">{t('Reading the correspondence…')}</p>
      </Sheet>
    );
  }

  const nc = t('not collected');
  const total = profile.messages_in + profile.messages_out;
  const thin = profile.insufficient;

  return (
    <Sheet pad>
      <div className="fact">
        <span className="fk">{t('messages')}</span>
        <span>
          <Fig>{profile.messages_in}</Fig> {t('from them')} · <Fig>{profile.messages_out}</Fig> {t('from you')}
        </span>
        <span className="fmeta voice-figure">{total}</span>
      </div>

      <div className="fact">
        <span className="fk">{t('rhythm')}</span>
        <span>
          {profile.median_reply_hours_ours !== null
            ? <>{t('you answer in')} <Fig unit={t('h')}>{profile.median_reply_hours_ours}</Fig></>
            : <>{t('you answer in')} <Unknown label={nc} /></>}
          {' · '}
          {profile.median_reply_hours_theirs !== null
            ? <>{t('they answer in')} <Fig unit={t('h')}>{profile.median_reply_hours_theirs}</Fig></>
            : <>{t('they answer in')} <Unknown label={nc} /></>}
        </span>
        <span className="fmeta">{t('median')}</span>
      </div>

      <div className="fact">
        <span className="fk">{t('length')}</span>
        <span>
          {profile.median_words_ours !== null
            ? <>{t('yours')} <Fig unit={t('words')}>{profile.median_words_ours}</Fig></>
            : <>{t('yours')} <Unknown label={nc} /></>}
          {' · '}
          {profile.median_words_theirs !== null
            ? <>{t('theirs')} <Fig unit={t('words')}>{profile.median_words_theirs}</Fig></>
            : <>{t('theirs')} <Unknown label={nc} /></>}
        </span>
        <span className="fmeta">{t('median')}</span>
      </div>

      <div className="fact">
        <span className="fk">{t('greeting')}</span>
        <span>
          {profile.greeting
            ? <span className="voice-record">«{profile.greeting}»</span>
            : <Unknown label={nc} />}
        </span>
        <span className="fmeta">{profile.greeting ? t('verbatim, your own') : t('no greeting on record')}</span>
      </div>

      <div className="fact">
        <span className="fk">{t('sign-off')}</span>
        <span>
          {profile.sign_off
            ? <span className="voice-record">«{profile.sign_off}»</span>
            : <Unknown label={nc} />}
        </span>
        <span className="fmeta">{profile.sign_off ? t('verbatim, your own') : t('no sign-off on record')}</span>
      </div>

      <div className="fact">
        <span className="fk">{t('register')}</span>
        <span>
          {profile.register ? (
            <>
              <Att kind="proposed" title={t('read from their own lines; nobody has held it')} />{' '}
              {t(REGISTER_WORD[profile.register] ?? profile.register)}
              {profile.language ? <> · {t(LANGUAGE_WORD[profile.language] ?? profile.language)}</> : null}
            </>
          ) : <Unknown label={nc} />}
        </span>
        <span className="fmeta voice-figure">
          {profile.register_evidence !== null
            ? t('{n} lines', { n: profile.register_evidence })
            : <Unknown label={t('no evidence')} />}
        </span>
      </div>

      {thin && (
        <p className="zone-empty">
          {t('Fewer than two messages with this person are on the record, so nothing above was measured. Every value reads "not collected" rather than zero: a profile guessed from one mail would send the next draft out in the wrong voice.')}
        </p>
      )}
    </Sheet>
  );
}
